import React, { useState, useEffect, useCallback } from 'react';
import { RefreshCw, X, Upload, Calendar, FileText, History as HistoryIcon, AlertTriangle, CheckCircle } from 'lucide-react';

// Staff Conversations — quarterly, 30-day and 90-day 1:1s for one manager's
// staff. Every endpoint is behind requireStaffConversationsAccess on the
// server; App.js only shows this screen to the same allowlist.
//
// Lifecycle state (needs scheduling / scheduled / overdue) is computed by the
// server and arrives on each item as `status`. This file never derives it.

const TYPE_LABELS = { quarterly: 'Quarterly', '30_day': '30-Day', '90_day': '90-Day' };
const TYPES = ['quarterly', '30_day', '90_day'];

const STATUS_STYLE = {
  overdue:          { bg: '#fee2e2', text: '#991b1b', border: '#fca5a5', label: 'Overdue' },
  scheduled:        { bg: '#dbeafe', text: '#1e40af', border: '#93c5fd', label: 'Scheduled' },
  needs_scheduling: { bg: '#fef3c7', text: '#92400e', border: '#fcd34d', label: 'Needs scheduling' },
  conducted:        { bg: '#dcfce7', text: '#15803d', border: '#86efac', label: 'Conducted' },
};

const CERT_STATUS_STYLE = {
  current: { text: '#15803d', label: 'Current' },
  expired: { text: '#991b1b', label: 'Expired' },
  missing: { text: '#6b7280', label: 'Not on file' },
};

const RELIABILITY_OPTIONS = [
  ['exceeds', 'Exceeds expectations'],
  ['meets', 'Meets expectations'],
  ['needs_improvement', 'Needs improvement'],
];
const OVERALL_OPTIONS = [
  ['meeting', 'Meeting expectations'],
  ['exceeding', 'Exceeding expectations'],
  ['development', 'Development needed'],
  ['followup', 'Follow-up required'],
];

// Form layout. Section 8 (certifications) is rendered separately — it is
// read live from Cert Tracking and never saved on the conversation.
const SECTIONS = [
  { title: '1. Patient Care & Safety', fields: [
    ['patient_care_going_well', 'What is going well with patient care?'],
    ['patient_care_improvements', 'What could be improved?'],
  ] },
  { title: '2. Teamwork & Communication', fields: [
    ['teamwork_strengths', 'Strengths'],
    ['teamwork_opportunities', 'Opportunities'],
  ] },
  { title: '3. Reliability & Professionalism', rating: 'reliability_rating', fields: [
    ['reliability_comments', 'Comments'],
  ] },
  { title: '4. Employee Voice', fields: [
    ['proud_of', 'What are you most proud of?'],
    ['challenges_and_support', 'Challenges, and support needed'],
  ] },
  { title: '5. Growth & Development', fields: [
    ['growth_development', 'Growth & development'],
  ] },
  { title: '6. Goals for Next Quarter', fields: [
    ['goal1', 'Goal 1'],
    ['goal2', 'Goal 2'],
    ['resources_needed', 'Resources needed'],
  ] },
  { title: '7. Recognition & Feedback', fields: [
    ['recognition', 'Recognition & feedback'],
  ] },
];
const SUMMARY_FIELDS = [
  ['manager_takeaway', 'Manager takeaway'],
  ['employee_comments', 'Employee comments'],
];
// 30/90-day orientation form — ONE progressive paper form. The 30-day (mid)
// meeting answers Q1-5; the 90-day (final) meeting answers Q6-7 with the
// 30-day answers shown above as read-only context. No Section 8, no Summary.
const ORIENTATION_TITLE = 'Mid/Final Orientation Evaluation for Core Nursing Competencies';
const ORIENTATION_30_FIELDS = [
  ['orientation_q1', '1. How do we compare with what we said in your interviewing process?'],
  ['orientation_q2', "2. What's working well?"],
  ['orientation_q3', '3. Have there been any individuals who have been helpful to you?'],
  ['orientation_q4', '4. Based on your prior work, what ideas for improvement do you have?'],
  ['orientation_q5', '5. Is there any reason that you feel this is not the right place for you?'],
];
const ORIENTATION_90_FIELDS = [
  ['orientation_q6', '6. Do you know of anyone who would be a good fit for our organization?'],
  ['orientation_q7', '7. As your supervisor, how can I help you?'],
];
const ORIENTATION_COMMENTS = ['orientation_additional_comments', 'Additional comments'];

const FORM_TEXT_FIELDS = [
  ...SECTIONS.flatMap((s) => s.fields.map(([k]) => k)),
  ...SUMMARY_FIELDS.map(([k]) => k),
  ...ORIENTATION_30_FIELDS.map(([k]) => k),
  ...ORIENTATION_90_FIELDS.map(([k]) => k),
  ORIENTATION_COMMENTS[0],
];

// The type can only change while a conversation is empty — otherwise answers
// written for one type would be hidden by the other's layout. To switch type
// on a conversation with answers, delete it and start a new one.
const hasAnyAnswer = (f) => !!f && (
  FORM_TEXT_FIELDS.some((k) => f[k] && String(f[k]).trim()) || !!f.reliability_rating || !!f.overall_status
);

// API returns YYYY-MM-DD strings. Reformat without new Date() to avoid a
// UTC-parse off-by-one.
const formatDate = (str) => {
  if (!str) return '—';
  const parts = String(str).slice(0, 10).split('-');
  if (parts.length !== 3) return str;
  const [y, m, d] = parts;
  return `${m}/${d}/${y}`;
};

const todayYMD = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const fullName = (r) => `${r.last_name}, ${r.first_name}`;

const readError = async (res) => {
  const j = await res.json().catch(() => ({}));
  return j.error || `Request failed (status ${res.status})`;
};

// ---------------------------------------------------------------------------
// Excel parsing. SheetJS is loaded with a dynamic import() so it lands in its
// own chunk and only downloads when Kasey actually uploads a sheet.
//
// Dates are read as raw serials and decoded with SSF.parse_date_code rather
// than cellDates:true — cellDates builds local-time Date objects, which can
// shift a hire date by a day depending on the browser's zone.
// ---------------------------------------------------------------------------
const UPLOAD_HEADERS = {
  lastname: 'last_name',
  firstname: 'first_name',
  jobtitle: 'job_title',
  shift: 'shift',
  hiredate: 'hire_date',
  '30dayconvo': 'convo_30',
  '90dayconvo': 'convo_90',
  quarterlyconvo: 'quarterly_convo',
};
const UPLOAD_HEADER_LABELS = {
  last_name: 'Last Name', first_name: 'First Name', job_title: 'Job Title', shift: 'Shift',
  hire_date: 'Hire Date', convo_30: '30 day convo', convo_90: '90 day convo', quarterly_convo: 'Quarterly Convo',
};
const DATE_KEYS = new Set(['hire_date', 'convo_30', 'convo_90']);
const normHeader = (h) => String(h == null ? '' : h).toLowerCase().replace(/[^a-z0-9]/g, '');
const pad2 = (n) => String(n).padStart(2, '0');

const cellToYmd = (v, XLSX, date1904) => {
  if (v == null || v === '') return null;
  if (typeof v === 'number') {
    const d = XLSX.SSF.parse_date_code(v, { date1904 });
    return d && d.y ? `${d.y}-${pad2(d.m)}-${pad2(d.d)}` : String(v);
  }
  const s = String(v).trim();
  if (!s) return null;
  let m = s.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{2}|\d{4})$/);
  if (m) {
    let y = Number(m[3]);
    if (m[3].length === 2) y += y < 50 ? 2000 : 1900;
    return `${y}-${pad2(m[1])}-${pad2(m[2])}`;
  }
  m = s.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
  if (m) return `${m[1]}-${pad2(m[2])}-${pad2(m[3])}`;
  return s; // not a date — the server reports it per row
};

async function parseStaffWorkbook(file) {
  const mod = await import('xlsx');
  const XLSX = mod.read ? mod : mod.default;
  const wb = XLSX.read(await file.arrayBuffer(), { type: 'array' });
  const date1904 = !!(wb.Workbook && wb.Workbook.WBProps && wb.Workbook.WBProps.date1904);
  const ws = wb.Sheets[wb.SheetNames[0]];
  if (!ws || !ws['!ref']) throw new Error('The first sheet is empty.');

  const firstRow = XLSX.utils.decode_range(ws['!ref']).s.r; // 0-based
  const grid = XLSX.utils.sheet_to_json(ws, { header: 1, raw: true, defval: null, blankrows: true });

  // The header row may sit under a title row — find it in the first 10 rows.
  const headerIdx = grid.slice(0, 10).findIndex((row) => {
    const n = (row || []).map(normHeader);
    return n.includes('lastname') && n.includes('firstname');
  });
  if (headerIdx === -1) throw new Error('Could not find a header row with "Last Name" and "First Name".');

  const colMap = {};
  grid[headerIdx].forEach((h, i) => {
    const key = UPLOAD_HEADERS[normHeader(h)];
    if (key && colMap[key] === undefined) colMap[key] = i;
  });
  const missing = Object.keys(UPLOAD_HEADER_LABELS).filter((k) => colMap[k] === undefined);
  if (missing.length) {
    throw new Error(`Missing column(s): ${missing.map((k) => UPLOAD_HEADER_LABELS[k]).join(', ')}`);
  }

  const rows = [];
  for (let i = headerIdx + 1; i < grid.length; i++) {
    const cells = grid[i] || [];
    const first = cells[colMap.first_name];
    const last = cells[colMap.last_name];
    if ((first == null || String(first).trim() === '') && (last == null || String(last).trim() === '')) continue;
    const out = { row: firstRow + i + 1 };
    Object.entries(colMap).forEach(([key, idx]) => {
      const v = cells[idx];
      out[key] = DATE_KEYS.has(key) ? cellToYmd(v, XLSX, date1904) : (v == null ? '' : String(v).trim());
    });
    rows.push(out);
  }
  if (rows.length === 0) throw new Error('No staff rows found under the header row.');
  return rows;
}

// ---------------------------------------------------------------------------
// Shared bits
// ---------------------------------------------------------------------------
const fieldLabel = { display: 'block', marginBottom: '0.375rem', fontSize: '0.875rem', fontWeight: '500', color: '#374151' };
const fieldInput = { width: '100%', padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '0.375rem', fontSize: '0.875rem', boxSizing: 'border-box', fontFamily: 'inherit' };
const btn = (variant = 'secondary') => ({
  padding: '0.5rem 0.9rem',
  borderRadius: '0.375rem',
  fontSize: '0.875rem',
  cursor: 'pointer',
  border: variant === 'secondary' ? '1px solid #d1d5db' : 'none',
  background: { primary: '#2563eb', success: '#16a34a', danger: '#dc2626', secondary: 'white' }[variant],
  color: variant === 'secondary' ? '#374151' : 'white',
  display: 'inline-flex', alignItems: 'center', gap: '0.375rem', whiteSpace: 'nowrap',
});
const smallBtn = { ...btn(), padding: '0.3rem 0.6rem', fontSize: '0.8rem' };
const th = { textAlign: 'left', padding: '0.6rem 0.75rem', fontSize: '0.75rem', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase', borderBottom: '1px solid #e5e7eb', whiteSpace: 'nowrap' };
const td = { padding: '0.6rem 0.75rem', fontSize: '0.875rem', color: '#1f2937', borderBottom: '1px solid #f3f4f6', verticalAlign: 'top' };

const StatusBadge = ({ status }) => {
  const s = STATUS_STYLE[status];
  if (!s) return null;
  return (
    <span style={{ display: 'inline-block', padding: '0.125rem 0.5rem', borderRadius: '999px', fontSize: '0.75rem', fontWeight: '600', background: s.bg, color: s.text, border: `1px solid ${s.border}`, whiteSpace: 'nowrap' }}>
      {s.label}
    </span>
  );
};

const ErrorBox = ({ children }) => (
  <div style={{ background: '#fee2e2', border: '1px solid #fca5a5', color: '#991b1b', padding: '0.75rem', borderRadius: '0.375rem', fontSize: '0.875rem', marginBottom: '1rem' }}>
    {children}
  </div>
);

// Module-level so inputs keep focus across parent re-renders.
const ModalShell = ({ title, onClose, children, footer, maxWidth = '520px' }) => (
  <div
    style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '1rem' }}
    onClick={onClose}
  >
    <div
      style={{ background: 'white', borderRadius: '0.75rem', maxWidth, width: '100%', maxHeight: '92vh', display: 'flex', flexDirection: 'column' }}
      onClick={(e) => e.stopPropagation()}
    >
      <div style={{ padding: '1.25rem 1.5rem', borderBottom: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '1rem' }}>
        <h2 style={{ fontSize: '1.2rem', fontWeight: '700', color: '#1f2937', margin: 0 }}>{title}</h2>
        <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '0.25rem', lineHeight: 0 }} aria-label="Close">
          <X size={22} color="#6b7280" />
        </button>
      </div>
      <div style={{ padding: '1.5rem', overflow: 'auto', flex: 1 }}>{children}</div>
      {footer && (
        <div style={{ padding: '1rem 1.5rem', borderTop: '1px solid #e5e7eb', display: 'flex', justifyContent: 'flex-end', gap: '0.75rem', flexWrap: 'wrap' }}>{footer}</div>
      )}
    </div>
  </div>
);

// ---------------------------------------------------------------------------
// Schedule modal
// ---------------------------------------------------------------------------
// `staff` is a /staff row. If an outstanding item of the chosen type already
// has a conversation row (scheduled or drafted), the date goes onto that row
// instead of creating a second one.
const ScheduleModal = ({ staff, initialType, onClose, onSaved }) => {
  const pendingFor = (type) => (staff.upcoming || []).find((it) => it.type === type && it.conversation_id);
  const firstItem = (type) => (staff.upcoming || []).find((it) => it.type === type);
  const [type, setType] = useState(initialType || (staff.upcoming && staff.upcoming[0] ? staff.upcoming[0].type : 'quarterly'));
  const [date, setDate] = useState(() => {
    const it = firstItem(initialType || type);
    return (it && it.scheduled_date) || '';
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const due = firstItem(type);
  const pending = pendingFor(type);

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      const res = pending
        ? await fetch(`/api/staff-conversations/${pending.conversation_id}`, {
            method: 'PUT', headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ scheduled_date: date }),
          })
        : await fetch('/api/staff-conversations', {
            method: 'POST', headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ cert_staff_id: staff.id, conversation_type: type, scheduled_date: date }),
          });
      if (!res.ok) throw new Error(await readError(res));
      onSaved();
    } catch (err) {
      setError(err.message);
      setSaving(false);
    }
  };

  return (
    <ModalShell
      title={`Schedule — ${staff.first_name} ${staff.last_name}`}
      onClose={onClose}
      footer={<>
        <button style={btn()} onClick={onClose}>Cancel</button>
        <button style={{ ...btn('primary'), opacity: date && !saving ? 1 : 0.5 }} disabled={!date || saving} onClick={save}>
          {saving ? 'Saving…' : 'Save'}
        </button>
      </>}
    >
      {error && <ErrorBox>{error}</ErrorBox>}
      <div style={{ marginBottom: '1rem' }}>
        <label style={fieldLabel}>Conversation type</label>
        <select style={fieldInput} value={type} onChange={(e) => {
          setType(e.target.value);
          const it = firstItem(e.target.value);
          setDate((it && it.scheduled_date) || '');
        }}>
          {TYPES.map((t) => <option key={t} value={t}>{TYPE_LABELS[t]}</option>)}
        </select>
        {due && due.due_date && (
          <div style={{ fontSize: '0.8rem', color: '#6b7280', marginTop: '0.375rem' }}>
            Due {formatDate(due.due_date)}{pending ? ' — updates the existing scheduled/draft conversation' : ''}
          </div>
        )}
      </div>
      <div>
        <label style={fieldLabel}>Meeting date</label>
        <input type="date" style={fieldInput} value={date} onChange={(e) => setDate(e.target.value)} />
      </div>
    </ModalShell>
  );
};

// ---------------------------------------------------------------------------
// 30/90-day orientation layout (used by ConversationForm)
// ---------------------------------------------------------------------------
const mutedNote = { fontSize: '0.875rem', color: '#6b7280', fontStyle: 'italic', border: '1px dashed #d1d5db', borderRadius: '0.5rem', padding: '0.75rem 1rem', marginBottom: '1rem' };

const OrientationSections = ({ type, form, person, preceptor, previous30, sectionBox, textArea }) => {
  const is90 = type === '90_day';
  const contextKeys = [...ORIENTATION_30_FIELDS.map(([k]) => k), ORIENTATION_COMMENTS[0]];
  const prevHasAnswers = !!previous30 && contextKeys.some((k) => previous30[k] && String(previous30[k]).trim());

  const meta = (label, value) => (
    <div>
      <div style={{ fontSize: '0.75rem', color: '#6b7280', textTransform: 'uppercase', fontWeight: '600' }}>{label}</div>
      <div style={{ fontSize: '0.875rem', color: '#1f2937' }}>{value}</div>
    </div>
  );

  // Three states for the 90-day context block: no 30-day at all, a 30-day
  // with no answers (imported from Excel), or the real answers read-only.
  let context = null;
  if (is90) {
    if (!previous30) {
      context = <div style={mutedNote}>30-day conversation not recorded</div>;
    } else if (!prevHasAnswers) {
      context = <div style={mutedNote}>30-day conversation held {formatDate(previous30.conducted_date)} — no answers recorded here</div>;
    } else {
      context = sectionBox(`30-Day (Mid) Answers — ${formatDate(previous30.conducted_date)} · read-only`, <>
        {[...ORIENTATION_30_FIELDS, ORIENTATION_COMMENTS].map(([k, label]) => (
          <div key={k} style={{ marginBottom: '0.9rem' }}>
            <div style={fieldLabel}>{label}</div>
            <div style={{ fontSize: '0.875rem', color: previous30[k] ? '#374151' : '#9ca3af', whiteSpace: 'pre-wrap', background: '#f9fafb', borderRadius: '0.375rem', padding: '0.5rem 0.625rem' }}>
              {previous30[k] || '—'}
            </div>
          </div>
        ))}
      </>);
    }
  }

  return (
    <>
      <div style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '0.5rem', padding: '1rem', marginBottom: '1rem' }}>
        <div style={{ fontWeight: '700', color: '#1f2937', marginBottom: '0.75rem' }}>{ORIENTATION_TITLE}</div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '0.75rem' }}>
          {meta('Preceptor', preceptor || <span style={{ color: '#9ca3af' }}>Not set — add in Cert Tracking → Edit Staff</span>)}
          {meta('Hire date', formatDate(person && person.hire_date))}
          {is90 && meta('Mid meeting (30-day)', previous30 ? formatDate(previous30.conducted_date) : '—')}
          {is90 && meta('Final meeting (90-day)', formatDate(form.conducted_date))}
        </div>
      </div>

      {!is90 && sectionBox('30-Day (Mid) Questions', <>
        {ORIENTATION_30_FIELDS.map(([k, label]) => textArea(k, label))}
        {textArea(...ORIENTATION_COMMENTS)}
      </>)}

      {is90 && <>
        {context}
        {sectionBox('90-Day (Final) Questions', <>
          {ORIENTATION_90_FIELDS.map(([k, label]) => textArea(k, label))}
          {textArea(...ORIENTATION_COMMENTS)}
        </>)}
      </>}
    </>
  );
};

// ---------------------------------------------------------------------------
// Conversation form modal
// ---------------------------------------------------------------------------
// Props:
//   conversationId  existing row to open, or null for a new conversation
//   staff           { id, first_name, last_name, job_title } — required when new
//   initialType     default type for a new conversation
//   readOnly        open in view mode (History); an Edit button unlocks it
const ConversationForm = ({ conversationId, staff, initialType, readOnly: startReadOnly, onClose, onSaved }) => {
  const id = conversationId || null;
  const [readOnly, setReadOnly] = useState(!!startReadOnly);
  const [form, setForm] = useState(null);
  const [person, setPerson] = useState(staff || null);
  const [section8, setSection8] = useState([]);
  const [previous30, setPrevious30] = useState(null);
  const [preceptor, setPreceptor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        if (conversationId) {
          const res = await fetch(`/api/staff-conversations/${conversationId}`);
          if (!res.ok) throw new Error(await readError(res));
          const { conversation, section8: certs, previous_30_day: prev } = await res.json();
          if (cancelled) return;
          const f = {
            conversation_type: conversation.conversation_type,
            scheduled_date: conversation.scheduled_date || '',
            conducted_date: conversation.conducted_date || '',
            reliability_rating: conversation.reliability_rating || '',
            overall_status: conversation.overall_status || '',
          };
          FORM_TEXT_FIELDS.forEach((k) => { f[k] = conversation[k] || ''; });
          setForm(f);
          setPerson({
            id: conversation.cert_staff_id,
            first_name: conversation.first_name,
            last_name: conversation.last_name,
            job_title: conversation.job_title,
            hire_date: conversation.hire_date,
          });
          setSection8(certs || []);
          setPrevious30(prev || null);
          setPreceptor(conversation.preceptor_name || null);
        } else {
          // Despite the name this returns all the form context a new
          // conversation needs: section8, previous_30_day, preceptor_name.
          const res = await fetch(`/api/staff-conversations/staff/${staff.id}/certs`);
          if (!res.ok) throw new Error(await readError(res));
          const { section8: certs, previous_30_day: prev, preceptor_name: preceptorName } = await res.json();
          if (cancelled) return;
          const f = {
            conversation_type: initialType || 'quarterly',
            scheduled_date: '', conducted_date: '', reliability_rating: '', overall_status: '',
          };
          FORM_TEXT_FIELDS.forEach((k) => { f[k] = ''; });
          setForm(f);
          setSection8(certs || []);
          setPrevious30(prev || null);
          setPreceptor(preceptorName || null);
        }
      } catch (err) {
        if (!cancelled) setLoadError(err.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [conversationId, staff, initialType]);

  const setField = (k, v) => setForm((prev) => ({ ...prev, [k]: v }));

  const save = async (markConducted) => {
    setSaving(true);
    setSaveError(null);
    try {
      const body = { ...form };
      if (markConducted && !body.conducted_date) body.conducted_date = todayYMD();
      if (body.conducted_date && body.conducted_date > todayYMD()) {
        throw new Error('Conducted date cannot be in the future.');
      }
      const res = id
        ? await fetch(`/api/staff-conversations/${id}`, {
            method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
          })
        : await fetch('/api/staff-conversations', {
            method: 'POST', headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ ...body, cert_staff_id: person.id }),
          });
      if (!res.ok) throw new Error(await readError(res));
      onSaved();
    } catch (err) {
      setSaveError(err.message);
      setSaving(false);
    }
  };

  const doDelete = async () => {
    setSaving(true);
    setSaveError(null);
    try {
      const res = await fetch(`/api/staff-conversations/${id}`, { method: 'DELETE' });
      if (!res.ok) throw new Error(await readError(res));
      onSaved();
    } catch (err) {
      setSaveError(err.message);
      setSaving(false);
    }
  };

  const isQuarterly = !form || form.conversation_type === 'quarterly';
  const title = person
    ? `${form ? TYPE_LABELS[form.conversation_type] + ' ' : ''}${isQuarterly ? 'Conversation' : 'Orientation Evaluation'} — ${person.first_name} ${person.last_name}`
    : 'Conversation';

  const isConducted = form && !!form.conducted_date;
  const typeLocked = hasAnyAnswer(form);

  let footer;
  if (loading || loadError || !form) {
    footer = <button style={btn()} onClick={onClose}>Close</button>;
  } else if (readOnly) {
    footer = <>
      <button style={btn()} onClick={onClose}>Close</button>
      <button style={btn('primary')} onClick={() => setReadOnly(false)}>Edit</button>
    </>;
  } else if (confirmDelete) {
    footer = <>
      <span style={{ alignSelf: 'center', fontSize: '0.875rem', color: '#991b1b', marginRight: 'auto' }}>Delete this conversation?</span>
      <button style={btn()} onClick={() => setConfirmDelete(false)} disabled={saving}>Keep it</button>
      <button style={btn('danger')} onClick={doDelete} disabled={saving}>{saving ? 'Deleting…' : 'Yes, delete'}</button>
    </>;
  } else {
    footer = <>
      {id && <button style={{ ...btn(), color: '#dc2626', marginRight: 'auto' }} onClick={() => setConfirmDelete(true)} disabled={saving}>Delete</button>}
      <button style={btn()} onClick={onClose} disabled={saving}>Cancel</button>
      <button style={btn('primary')} onClick={() => save(false)} disabled={saving}>{saving ? 'Saving…' : (isConducted ? 'Save' : 'Save Draft')}</button>
      {!isConducted && (
        <button style={btn('success')} onClick={() => save(true)} disabled={saving}>
          <CheckCircle size={16} /> Mark Conducted &amp; Save
        </button>
      )}
    </>;
  }

  const textArea = (k, label) => (
    <div key={k} style={{ marginBottom: '0.9rem' }}>
      <label style={fieldLabel}>{label}</label>
      {readOnly
        ? <div style={{ fontSize: '0.875rem', color: form[k] ? '#1f2937' : '#9ca3af', whiteSpace: 'pre-wrap', padding: '0.25rem 0' }}>{form[k] || '—'}</div>
        : <textarea style={{ ...fieldInput, minHeight: '64px', resize: 'vertical' }} value={form[k]} onChange={(e) => setField(k, e.target.value)} />}
    </div>
  );

  const radioGroup = (k, options) => (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem 1.25rem', marginBottom: '0.9rem' }}>
      {readOnly
        ? <div style={{ fontSize: '0.875rem', color: form[k] ? '#1f2937' : '#9ca3af' }}>
            {(options.find(([v]) => v === form[k]) || [null, '—'])[1]}
          </div>
        : <>
            {options.map(([v, label]) => (
              <label key={v} style={{ display: 'inline-flex', alignItems: 'center', gap: '0.375rem', fontSize: '0.875rem', cursor: 'pointer' }}>
                <input type="radio" name={k} checked={form[k] === v} onChange={() => setField(k, v)} /> {label}
              </label>
            ))}
            {form[k] && (
              <button type="button" style={{ ...smallBtn, padding: '0.1rem 0.5rem' }} onClick={() => setField(k, '')}>Clear</button>
            )}
          </>}
    </div>
  );

  const sectionBox = (titleText, children) => (
    <div key={titleText} style={{ border: '1px solid #e5e7eb', borderRadius: '0.5rem', padding: '1rem 1rem 0.25rem', marginBottom: '1rem' }}>
      <h3 style={{ margin: '0 0 0.75rem', fontSize: '0.95rem', fontWeight: '700', color: '#1f2937' }}>{titleText}</h3>
      {children}
    </div>
  );

  return (
    <ModalShell title={title} onClose={onClose} footer={footer} maxWidth="820px">
      {loading && <div style={{ color: '#6b7280' }}>Loading…</div>}
      {loadError && <ErrorBox>{loadError}</ErrorBox>}
      {form && !loading && (
        <>
          {saveError && <ErrorBox>{saveError}</ErrorBox>}
          {person && person.job_title && (
            <div style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: '1rem' }}>{person.job_title}</div>
          )}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '0.75rem', marginBottom: '1.25rem' }}>
            <div>
              <label style={fieldLabel}>Type</label>
              {readOnly
                ? <div style={{ fontSize: '0.875rem' }}>{TYPE_LABELS[form.conversation_type]}</div>
                : <>
                    <select
                      style={{ ...fieldInput, background: typeLocked ? '#f3f4f6' : 'white' }}
                      value={form.conversation_type}
                      disabled={typeLocked}
                      onChange={(e) => setField('conversation_type', e.target.value)}
                    >
                      {TYPES.map((t) => <option key={t} value={t}>{TYPE_LABELS[t]}</option>)}
                    </select>
                    {typeLocked && (
                      <div style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.25rem' }}>
                        Locked once answers are entered. Delete and start over to change type.
                      </div>
                    )}
                  </>}
            </div>
            <div>
              <label style={fieldLabel}>Scheduled date</label>
              {readOnly
                ? <div style={{ fontSize: '0.875rem' }}>{formatDate(form.scheduled_date)}</div>
                : <input type="date" style={fieldInput} value={form.scheduled_date} onChange={(e) => setField('scheduled_date', e.target.value)} />}
            </div>
            <div>
              <label style={fieldLabel}>Conducted date</label>
              {readOnly
                ? <div style={{ fontSize: '0.875rem' }}>{formatDate(form.conducted_date)}</div>
                : <input type="date" style={fieldInput} value={form.conducted_date} max={todayYMD()} onChange={(e) => setField('conducted_date', e.target.value)} />}
            </div>
          </div>

          {isQuarterly && <>
          {SECTIONS.map((s) => sectionBox(s.title, <>
            {s.rating && radioGroup(s.rating, RELIABILITY_OPTIONS)}
            {s.fields.map(([k, label]) => textArea(k, label))}
          </>))}

          {sectionBox('8. Certifications', <>
            <div style={{ overflowX: 'auto', marginBottom: '0.5rem' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr><th style={th}>Cert</th><th style={th}>Earned</th><th style={th}>Expires</th><th style={th}>Status</th></tr>
                </thead>
                <tbody>
                  {section8.map((c) => {
                    const st = CERT_STATUS_STYLE[c.status] || CERT_STATUS_STYLE.missing;
                    return (
                      <tr key={c.cert_type_id}>
                        <td style={td}><strong>{c.abbreviation}</strong></td>
                        <td style={td}>{formatDate(c.date_earned)}</td>
                        <td style={td}>{formatDate(c.date_expires)}</td>
                        <td style={{ ...td, color: st.text, fontWeight: '600' }}>{st.label}</td>
                      </tr>
                    );
                  })}
                  {section8.length === 0 && <tr><td style={td} colSpan={4}>No cert types configured.</td></tr>}
                </tbody>
              </table>
            </div>
            <p style={{ fontSize: '0.8rem', color: '#6b7280', margin: '0 0 0.75rem' }}>
              Read-only. Update dates in the Cert Tracking Dashboard if needed.
            </p>
          </>)}

          {sectionBox('Summary', <>
            <label style={fieldLabel}>Overall status</label>
            {radioGroup('overall_status', OVERALL_OPTIONS)}
            {SUMMARY_FIELDS.map(([k, label]) => textArea(k, label))}
          </>)}
          </>}

          {!isQuarterly && (
            <OrientationSections
              type={form.conversation_type}
              form={form}
              person={person}
              preceptor={preceptor}
              previous30={previous30}
              sectionBox={sectionBox}
              textArea={textArea}
            />
          )}
        </>
      )}
    </ModalShell>
  );
};

// ---------------------------------------------------------------------------
// Upload modal
// ---------------------------------------------------------------------------
const UploadModal = ({ onClose, onDone }) => {
  const [file, setFile] = useState(null);
  const [rows, setRows] = useState(null);
  const [parsing, setParsing] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);

  const pickFile = async (f) => {
    setFile(f);
    setRows(null);
    setResult(null);
    setError(null);
    if (!f) return;
    setParsing(true);
    try {
      setRows(await parseStaffWorkbook(f));
    } catch (err) {
      setError(err.message || 'Could not read that file.');
    } finally {
      setParsing(false);
    }
  };

  const upload = async () => {
    setUploading(true);
    setError(null);
    try {
      const res = await fetch('/api/staff-conversations/upload', {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ rows }),
      });
      if (!res.ok) throw new Error(await readError(res));
      setResult(await res.json());
      onDone();
    } catch (err) {
      setError(err.message);
    } finally {
      setUploading(false);
    }
  };

  const stat = (label, value, color = '#1f2937') => (
    <div style={{ border: '1px solid #e5e7eb', borderRadius: '0.5rem', padding: '0.75rem', textAlign: 'center' }}>
      <div style={{ fontSize: '1.5rem', fontWeight: '700', color }}>{value}</div>
      <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>{label}</div>
    </div>
  );

  return (
    <ModalShell
      title="Upload Staff"
      onClose={onClose}
      maxWidth="640px"
      footer={result
        ? <button style={btn('primary')} onClick={onClose}>Done</button>
        : <>
            <button style={btn()} onClick={onClose}>Cancel</button>
            <button style={{ ...btn('primary'), opacity: rows && !uploading ? 1 : 0.5 }} disabled={!rows || uploading} onClick={upload}>
              <Upload size={16} /> {uploading ? 'Uploading…' : `Upload ${rows ? rows.length : ''} rows`}
            </button>
          </>}
    >
      {error && <ErrorBox>{error}</ErrorBox>}

      {!result && (
        <>
          <p style={{ fontSize: '0.875rem', color: '#374151', marginTop: 0 }}>
            Excel (.xlsx) with columns: Last Name, First Name, Job Title, Shift, Hire Date,
            30 day convo, 90 day convo, Quarterly Convo. People are matched to Cert Tracking by name —
            nobody new is added. Dates in the 30/90 day columns are recorded as conversations already held.
          </p>
          <input
            type="file"
            accept=".xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            onChange={(e) => pickFile(e.target.files && e.target.files[0])}
          />
          {parsing && <div style={{ marginTop: '0.75rem', color: '#6b7280', fontSize: '0.875rem' }}>Reading {file && file.name}…</div>}
          {rows && (
            <div style={{ marginTop: '0.75rem', fontSize: '0.875rem', color: '#15803d' }}>
              Read {rows.length} staff rows from {file && file.name}. Nothing is saved until you click Upload.
            </div>
          )}
        </>
      )}

      {result && (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(110px, 1fr))', gap: '0.75rem', marginBottom: '1.25rem' }}>
            {stat('Matched', result.matched, '#15803d')}
            {stat('Updated', result.updated)}
            {stat('Unchanged', result.unchanged)}
            {stat('Unmatched', result.unmatched.length, result.unmatched.length ? '#991b1b' : '#1f2937')}
            {stat('30/90 imported', result.conversations_imported)}
          </div>

          {result.unmatched.length > 0 && (
            <div style={{ border: '1px solid #fca5a5', background: '#fef2f2', borderRadius: '0.5rem', padding: '0.75rem 1rem', marginBottom: '1rem' }}>
              <div style={{ fontWeight: '700', color: '#991b1b', marginBottom: '0.5rem', display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
                <AlertTriangle size={16} /> Not matched — fix the spelling in Excel or add them in Cert Tracking
              </div>
              <ul style={{ margin: 0, paddingLeft: '1.25rem', fontSize: '0.875rem' }}>
                {result.unmatched.map((u, i) => (
                  <li key={i}><strong>{u.last}, {u.first}</strong> — row {u.row}{u.reason ? ` (${u.reason})` : ''}</li>
                ))}
              </ul>
            </div>
          )}

          {result.errors.length > 0 && (
            <div style={{ border: '1px solid #fcd34d', background: '#fffbeb', borderRadius: '0.5rem', padding: '0.75rem 1rem', marginBottom: '1rem' }}>
              <div style={{ fontWeight: '700', color: '#92400e', marginBottom: '0.5rem' }}>Row notes</div>
              <ul style={{ margin: 0, paddingLeft: '1.25rem', fontSize: '0.875rem' }}>
                {result.errors.map((e, i) => (
                  <li key={i}>Row {e.row}{e.last ? ` (${e.last}, ${e.first})` : ''}: {e.message}</li>
                ))}
              </ul>
            </div>
          )}

          {result.unmatched.length === 0 && result.errors.length === 0 && (
            <div style={{ color: '#15803d', fontSize: '0.875rem', display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
              <CheckCircle size={16} /> Every row matched.
            </div>
          )}
          {result.conversations_already_present > 0 && (
            <div style={{ color: '#6b7280', fontSize: '0.8rem', marginTop: '0.5rem' }}>
              {result.conversations_already_present} 30/90-day date(s) were already recorded and skipped.
            </div>
          )}
        </>
      )}
    </ModalShell>
  );
};

// ---------------------------------------------------------------------------
// Main screen
// ---------------------------------------------------------------------------
const tabBtn = (active) => ({
  padding: '0.6rem 1rem', border: 'none', background: 'none', cursor: 'pointer', fontSize: '0.9rem',
  fontWeight: active ? '700' : '500', color: active ? '#2563eb' : '#6b7280',
  borderBottom: active ? '2px solid #2563eb' : '2px solid transparent', marginBottom: '-1px',
  display: 'inline-flex', alignItems: 'center', gap: '0.375rem',
});

const EMPTY_HISTORY_FILTERS = { staff_id: '', type: '', from: '', to: '' };

const StaffConversations = () => {
  const [tab, setTab] = useState('staff');
  const [staff, setStaff] = useState([]);
  const [upcoming, setUpcoming] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [history, setHistory] = useState({ rows: [], total: 0, page: 1, page_size: 25, staff_options: [] });
  const [historyFilters, setHistoryFilters] = useState(EMPTY_HISTORY_FILTERS);
  const [historyPage, setHistoryPage] = useState(1);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState(null);

  // { kind: 'schedule'|'form'|'upload', ...props }
  const [modal, setModal] = useState(null);

  const fetchMain = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [staffRes, upRes] = await Promise.all([
        fetch('/api/staff-conversations/staff'),
        fetch('/api/staff-conversations/upcoming'),
      ]);
      if (!staffRes.ok) throw new Error(await readError(staffRes));
      if (!upRes.ok) throw new Error(await readError(upRes));
      const staffJson = await staffRes.json();
      const upJson = await upRes.json();
      setStaff(Array.isArray(staffJson.staff) ? staffJson.staff : []);
      setUpcoming(Array.isArray(upJson.items) ? upJson.items : []);
    } catch (err) {
      setError(err.message || 'Failed to load staff conversations');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchHistory = useCallback(async () => {
    setHistoryLoading(true);
    setHistoryError(null);
    try {
      const qs = new URLSearchParams({ page: String(historyPage), page_size: '25' });
      Object.entries(historyFilters).forEach(([k, v]) => { if (v) qs.set(k, v); });
      const res = await fetch(`/api/staff-conversations/history?${qs.toString()}`);
      if (!res.ok) throw new Error(await readError(res));
      setHistory(await res.json());
    } catch (err) {
      setHistoryError(err.message || 'Failed to load history');
    } finally {
      setHistoryLoading(false);
    }
  }, [historyFilters, historyPage]);

  useEffect(() => { fetchMain(); }, [fetchMain]);
  useEffect(() => { if (tab === 'history') fetchHistory(); }, [tab, fetchHistory]);

  const refreshAll = () => {
    fetchMain();
    if (tab === 'history') fetchHistory();
  };

  const closeModal = () => setModal(null);
  const savedAndClose = () => { setModal(null); refreshAll(); };

  const staffById = new Map(staff.map((s) => [s.id, s]));

  const openSchedule = (s, type) => setModal({ kind: 'schedule', staff: s, initialType: type });
  // Start Conversation resumes an existing scheduled/draft row of that type
  // when there is one, so the form fills in the meeting Kasey already set up.
  const openStart = (s, item) => {
    const it = item || (s.upcoming && s.upcoming[0]);
    if (it && it.conversation_id) {
      setModal({ kind: 'form', conversationId: it.conversation_id });
    } else {
      setModal({ kind: 'form', staff: s, initialType: it ? it.type : 'quarterly' });
    }
  };
  const openHistoryFor = (s) => {
    setHistoryFilters({ ...EMPTY_HISTORY_FILTERS, staff_id: String(s.id) });
    setHistoryPage(1);
    setTab('history');
  };

  const overdueCount = upcoming.filter((i) => i.status === 'overdue').length;
  const totalPages = Math.max(1, Math.ceil((history.total || 0) / (history.page_size || 25)));

  return (
    <div className="content-card" style={{ padding: '1.5rem' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '1rem', flexWrap: 'wrap', marginBottom: '1rem' }}>
        <div>
          <h2 className="content-title" style={{ margin: 0 }}>Staff Conversations</h2>
          <p style={{ margin: '0.25rem 0 0', color: '#6b7280', fontSize: '0.875rem' }}>
            Quarterly, 30-day and 90-day check-ins
          </p>
        </div>
        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
          <button style={btn()} onClick={refreshAll} disabled={loading}>
            <RefreshCw size={16} /> Refresh
          </button>
          <button style={btn('primary')} onClick={() => setModal({ kind: 'upload' })}>
            <Upload size={16} /> Upload Staff
          </button>
        </div>
      </div>

      <div style={{ borderBottom: '1px solid #e5e7eb', marginBottom: '1rem', display: 'flex', gap: '0.25rem', flexWrap: 'wrap' }}>
        <button style={tabBtn(tab === 'staff')} onClick={() => setTab('staff')}>My Staff ({staff.length})</button>
        <button style={tabBtn(tab === 'upcoming')} onClick={() => setTab('upcoming')}>
          <Calendar size={15} /> Upcoming ({upcoming.length})
          {overdueCount > 0 && (
            <span style={{ background: '#dc2626', color: 'white', borderRadius: '999px', fontSize: '0.7rem', padding: '0 0.4rem' }}>{overdueCount}</span>
          )}
        </button>
        <button style={tabBtn(tab === 'history')} onClick={() => setTab('history')}><HistoryIcon size={15} /> History</button>
      </div>

      {error && <ErrorBox>{error}</ErrorBox>}

      {/* ---------- My Staff ---------- */}
      {tab === 'staff' && (
        loading ? <div style={{ color: '#6b7280' }}>Loading…</div>
        : staff.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '2.5rem 1rem', color: '#6b7280' }}>
            <p style={{ marginTop: 0 }}>No staff yet. Staff appear here once they have a hire date.</p>
            <button style={btn('primary')} onClick={() => setModal({ kind: 'upload' })}>
              <Upload size={16} /> Upload your staff Excel
            </button>
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={th}>Name</th>
                  <th style={th}>Role</th>
                  <th style={th}>Shift</th>
                  <th style={th}>Hire date</th>
                  <th style={th}>Quarterly cycle</th>
                  <th style={th}>Next due</th>
                  <th style={th}>Last quarterly</th>
                  <th style={th}></th>
                </tr>
              </thead>
              <tbody>
                {staff.map((s) => {
                  const next = s.upcoming && s.upcoming[0];
                  return (
                    <tr key={s.id}>
                      <td style={{ ...td, fontWeight: '600' }}>{fullName(s)}</td>
                      <td style={td}>{s.job_title || '—'}</td>
                      <td style={td}>{s.shift || '—'}</td>
                      <td style={td}>{formatDate(s.hire_date)}</td>
                      <td style={td}>{s.quarterly_cycle_label || <span style={{ color: '#9ca3af' }}>None</span>}</td>
                      <td style={td}>
                        {next ? (
                          <div>
                            <div>{TYPE_LABELS[next.type]} — {formatDate(next.effective_date)}</div>
                            <div style={{ marginTop: '0.25rem' }}><StatusBadge status={next.status} /></div>
                            {s.upcoming.length > 1 && (
                              <div style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.25rem' }}>+{s.upcoming.length - 1} more</div>
                            )}
                          </div>
                        ) : <span style={{ color: '#9ca3af' }}>Nothing due</span>}
                      </td>
                      <td style={td}>{formatDate(s.last_conducted && s.last_conducted.quarterly)}</td>
                      <td style={{ ...td, whiteSpace: 'nowrap' }}>
                        <div style={{ display: 'flex', gap: '0.375rem', flexWrap: 'wrap' }}>
                          <button style={smallBtn} onClick={() => openSchedule(s, next && next.type)}><Calendar size={14} /> Schedule</button>
                          <button style={smallBtn} onClick={() => openStart(s)}><FileText size={14} /> Start Conversation</button>
                          <button style={smallBtn} onClick={() => openHistoryFor(s)}><HistoryIcon size={14} /> History</button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )
      )}

      {/* ---------- Upcoming ---------- */}
      {tab === 'upcoming' && (
        loading ? <div style={{ color: '#6b7280' }}>Loading…</div>
        : upcoming.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '2.5rem 1rem', color: '#6b7280' }}>
            Nothing due in the next 30 days and nothing overdue.
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={th}>Date</th>
                  <th style={th}>Staff</th>
                  <th style={th}>Type</th>
                  <th style={th}>Due</th>
                  <th style={th}>Scheduled</th>
                  <th style={th}>Status</th>
                  <th style={th}></th>
                </tr>
              </thead>
              <tbody>
                {upcoming.map((it, i) => {
                  const s = staffById.get(it.cert_staff_id);
                  return (
                    <tr key={`${it.cert_staff_id}-${it.type}-${it.conversation_id || it.due_date || i}`}>
                      <td style={{ ...td, fontWeight: '600' }}>{formatDate(it.effective_date)}</td>
                      <td style={td}>{fullName(it)}{it.job_title ? <span style={{ color: '#6b7280' }}> · {it.job_title}</span> : null}</td>
                      <td style={td}>{TYPE_LABELS[it.type]}</td>
                      <td style={td}>{formatDate(it.due_date)}</td>
                      <td style={td}>{it.scheduled_date ? formatDate(it.scheduled_date) : <span style={{ color: '#92400e' }}>Needs scheduling</span>}</td>
                      <td style={td}><StatusBadge status={it.status} /></td>
                      <td style={{ ...td, whiteSpace: 'nowrap' }}>
                        <div style={{ display: 'flex', gap: '0.375rem' }}>
                          {s && (
                            <button style={smallBtn} onClick={() => openSchedule(s, it.type)}>
                              <Calendar size={14} /> {it.scheduled_date ? 'Reschedule' : 'Schedule'}
                            </button>
                          )}
                          {s && (
                            <button style={smallBtn} onClick={() => openStart(s, it)}><FileText size={14} /> Open Form</button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )
      )}

      {/* ---------- History ---------- */}
      {tab === 'history' && (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '0.75rem', marginBottom: '1rem', alignItems: 'end' }}>
            <div>
              <label style={fieldLabel}>Staff</label>
              <select style={fieldInput} value={historyFilters.staff_id} onChange={(e) => { setHistoryFilters((f) => ({ ...f, staff_id: e.target.value })); setHistoryPage(1); }}>
                <option value="">Everyone</option>
                {(history.staff_options || []).map((o) => <option key={o.id} value={String(o.id)}>{fullName(o)}</option>)}
                {/* Keep a staff filter chosen from My Staff selectable even before they have history */}
                {historyFilters.staff_id && !(history.staff_options || []).some((o) => String(o.id) === historyFilters.staff_id) && staffById.get(Number(historyFilters.staff_id)) && (
                  <option value={historyFilters.staff_id}>{fullName(staffById.get(Number(historyFilters.staff_id)))}</option>
                )}
              </select>
            </div>
            <div>
              <label style={fieldLabel}>Type</label>
              <select style={fieldInput} value={historyFilters.type} onChange={(e) => { setHistoryFilters((f) => ({ ...f, type: e.target.value })); setHistoryPage(1); }}>
                <option value="">All types</option>
                {TYPES.map((t) => <option key={t} value={t}>{TYPE_LABELS[t]}</option>)}
              </select>
            </div>
            <div>
              <label style={fieldLabel}>From</label>
              <input type="date" style={fieldInput} value={historyFilters.from} onChange={(e) => { setHistoryFilters((f) => ({ ...f, from: e.target.value })); setHistoryPage(1); }} />
            </div>
            <div>
              <label style={fieldLabel}>To</label>
              <input type="date" style={fieldInput} value={historyFilters.to} onChange={(e) => { setHistoryFilters((f) => ({ ...f, to: e.target.value })); setHistoryPage(1); }} />
            </div>
            <div>
              <button style={btn()} onClick={() => { setHistoryFilters(EMPTY_HISTORY_FILTERS); setHistoryPage(1); }}>Clear filters</button>
            </div>
          </div>

          {historyError && <ErrorBox>{historyError}</ErrorBox>}
          {historyLoading ? <div style={{ color: '#6b7280' }}>Loading…</div>
            : history.rows.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '2rem 1rem', color: '#6b7280' }}>No conducted conversations match these filters.</div>
            ) : (
              <>
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr>
                        <th style={th}>Conducted</th>
                        <th style={th}>Staff</th>
                        <th style={th}>Type</th>
                        <th style={th}>Overall status</th>
                        <th style={th}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {history.rows.map((r) => (
                        <tr key={r.id} style={{ cursor: 'pointer' }} onClick={() => setModal({ kind: 'form', conversationId: r.id, readOnly: true })}>
                          <td style={{ ...td, fontWeight: '600' }}>{formatDate(r.conducted_date)}</td>
                          <td style={td}>
                            {fullName(r)}
                            {Number(r.staff_active) !== 1 && <span style={{ color: '#9ca3af' }}> (inactive)</span>}
                          </td>
                          <td style={td}>{TYPE_LABELS[r.conversation_type]}</td>
                          <td style={td}>{(OVERALL_OPTIONS.find(([v]) => v === r.overall_status) || [null, '—'])[1]}</td>
                          <td style={td}><span style={{ color: '#2563eb', fontSize: '0.8rem' }}>View</span></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '0.75rem', fontSize: '0.875rem', color: '#6b7280', flexWrap: 'wrap', gap: '0.5rem' }}>
                  <span>{history.total} conversation{history.total === 1 ? '' : 's'}</span>
                  <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                    <button style={smallBtn} disabled={historyPage <= 1} onClick={() => setHistoryPage((p) => p - 1)}>Previous</button>
                    <span>Page {historyPage} of {totalPages}</span>
                    <button style={smallBtn} disabled={historyPage >= totalPages} onClick={() => setHistoryPage((p) => p + 1)}>Next</button>
                  </div>
                </div>
              </>
            )}
        </>
      )}

      {modal && modal.kind === 'schedule' && (
        <ScheduleModal staff={modal.staff} initialType={modal.initialType} onClose={closeModal} onSaved={savedAndClose} />
      )}
      {modal && modal.kind === 'form' && (
        <ConversationForm
          conversationId={modal.conversationId}
          staff={modal.staff}
          initialType={modal.initialType}
          readOnly={modal.readOnly}
          onClose={closeModal}
          onSaved={savedAndClose}
        />
      )}
      {modal && modal.kind === 'upload' && (
        <UploadModal onClose={closeModal} onDone={fetchMain} />
      )}
    </div>
  );
};

export default StaffConversations;
